import type { AppetiteMetricStatus, RiskAppetiteMetric } from "./risk-appetite-static-data";

const SEVERITY_RANK: Record<AppetiteMetricStatus, number> = {
  Within: 0,
  Pending: 1,
  EWI: 2,
  "At Limit": 3,
  Recovery: 4,
  Capacity: 5,
};

export function getMetricStatus(metric: RiskAppetiteMetric): AppetiteMetricStatus {
  const { actualValue, comparator, thresholds } = metric;
  if (actualValue === null) {
    return "Pending";
  }

  const levelEntries: Array<{ status: AppetiteMetricStatus; value: number | null }> = [
    { status: "Capacity", value: thresholds.level4.value },
    { status: "Recovery", value: thresholds.level3.value },
    { status: "At Limit", value: thresholds.level2.value },
    { status: "EWI", value: thresholds.level1.value },
  ];

  const hasAnyThreshold = levelEntries.some((entry) => entry.value !== null);
  if (!hasAnyThreshold) {
    return "Pending";
  }

  for (const entry of levelEntries) {
    if (entry.value === null) {
      continue;
    }
    const breached = comparator === "min" ? actualValue < entry.value : actualValue > entry.value;
    if (breached) {
      return entry.status;
    }
  }

  return "Within";
}

export function getCategoryStatus(metrics: RiskAppetiteMetric[], categoryId: string): AppetiteMetricStatus {
  const categoryMetrics = metrics.filter((metric) => metric.categoryId === categoryId);
  if (!categoryMetrics.length) {
    return "Pending";
  }

  return categoryMetrics.reduce<AppetiteMetricStatus>((currentWorst, metric) => {
    const metricStatus = getMetricStatus(metric);
    return SEVERITY_RANK[metricStatus] > SEVERITY_RANK[currentWorst] ? metricStatus : currentWorst;
  }, "Within");
}

export function getProfileStatus(metrics: RiskAppetiteMetric[]): AppetiteMetricStatus {
  if (!metrics.length) {
    return "Pending";
  }
  return metrics.reduce<AppetiteMetricStatus>((currentWorst, metric) => {
    const metricStatus = getMetricStatus(metric);
    return SEVERITY_RANK[metricStatus] > SEVERITY_RANK[currentWorst] ? metricStatus : currentWorst;
  }, "Within");
}

export function formatMetricValue(metric: RiskAppetiteMetric, value: number | null): string {
  if (value === null) {
    return "TBD";
  }
  if (metric.unit === "percent") {
    return `${value}%`;
  }
  if (metric.unit === "days") {
    return `${value} Days`;
  }
  if (metric.unit === "gbp_million_buffer") {
    return `TCR + buffers + GBP${value}m`;
  }
  return `${value}`;
}

